import {
  Tags,
  TagsContent,
  TagsEmpty,
  TagsItem,
  TagsList,
  TagsTrigger,
  TagsValue,
} from '@/components/ui/kibo-ui/tags';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useReactFlow } from '@xyflow/react';
import type { ChangeEventHandler } from 'react';
import type { MemoryNodeProps } from '.';
import { NodeLayout } from '../layout';

type MemoryPrimitiveProps = MemoryNodeProps & {
  title: string;
};

const PRESET_TAGS = [
  'notes',
  'research',
  'ideas',
  'documentation',
  'meeting',
  'project',
  'personal',
  'work',
  'important',
  'draft',
  'reference',
  'todo',
  'creative',
  'learning',
  'archive',
];

export const MemoryPrimitive = ({
  data,
  id,
  type,
  title,
}: MemoryPrimitiveProps) => {
  const { updateNodeData } = useReactFlow();

  const handleContentChange: ChangeEventHandler<HTMLTextAreaElement> = (
    event
  ) => {
    updateNodeData(id, { content: event.target.value });
  };

  const handleTagSelect = (tag: string) => {
    const currentTags = data.tags || [];
    if (!currentTags.includes(tag)) {
      updateNodeData(id, { tags: [...currentTags, tag] });
    }
  };

  const handleTagRemove = (tagToRemove: string) => {
    const currentTags = data.tags || [];
    updateNodeData(id, {
      tags: currentTags.filter((tag) => tag !== tagToRemove),
    });
  };

  const availableTags = PRESET_TAGS.filter(
    (tag) => !(data.tags || []).includes(tag)
  );

  return (
    <NodeLayout
      className="node-memory"
      id={id}
      data={data}
      title={title}
      type={type}
    >
      <div className="space-y-4 p-2">
        <div className="space-y-2">
          <Label htmlFor={`${id}-content`} className="font-medium text-sm">
            Content to Save
          </Label>
          <Textarea
            id={`${id}-content`}
            placeholder="Enter the content you want to save to memory..."
            value={data.content || ''}
            onChange={handleContentChange}
            className="min-h-[100px] resize-none"
          />
        </div>

        <div className="space-y-2">
          <Label className="font-medium text-sm">Tags (optional)</Label>

          <Tags>
            <TagsTrigger>
              {data.tags?.map((tag) => (
                <TagsValue
                  key={tag}
                  onRemove={() => handleTagRemove(tag)}
                  className="bg-sky-50 text-sky-600 hover:bg-sky-100 dark:bg-sky-900/20 dark:text-sky-400 dark:hover:bg-sky-900/30"
                >
                  {tag}
                </TagsValue>
              ))}
            </TagsTrigger>
            <TagsContent>
              <TagsList>
                <TagsEmpty>No tags found.</TagsEmpty>
                {availableTags.map((tag) => (
                  <TagsItem key={tag} onSelect={() => handleTagSelect(tag)}>
                    {tag}
                  </TagsItem>
                ))}
              </TagsList>
            </TagsContent>
          </Tags>

          <p className="text-muted-foreground text-xs">
            Select from preset tags to organize your memories
          </p>
        </div>
      </div>
    </NodeLayout>
  );
};
